import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Img } from "@/components/ui/img";
import { uploadFile } from "@/lib/upload";
import { toast } from "@/lib/toast";
import { CircleNotch, X, ImageSquare } from "@phosphor-icons/react";
import { isValidProfileVideoUrl, PROFILE_VIDEO_ERROR } from "@/schemas/therapist";
import type { EnrollFormData, UpdateField } from "./types";

export function StepProfile({
  form,
  updateField,
}: {
  form: EnrollFormData;
  updateField: UpdateField;
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  async function handlePhotoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    setIsUploading(true);
    try {
      const result = await uploadFile(file, "profiles");
      updateField("profileImageUrl", result.fileUrl);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Greška pri uploadu.";
      setUploadError(msg);
      toast.error(msg);
    } finally {
      setIsUploading(false);
      e.target.value = "";
    }
  }

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label>
          Fotografija <span className="text-rose-500">*</span>
        </Label>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handlePhotoUpload}
          className="hidden"
          id="profile-photo-input"
        />

        {form.profileImageUrl ? (
          <div className="group relative inline-block overflow-hidden rounded-2xl border">
            <Img
              src={form.profileImageUrl}
              alt="Profilna"
              className="h-48 w-40 object-cover sm:h-56 sm:w-48"
            />
            <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/0 opacity-0 transition-all duration-200 group-hover:bg-black/40 group-hover:opacity-100">
              <Label
                htmlFor="profile-photo-input"
                className="cursor-pointer rounded-lg bg-white/90 px-3 py-1.5 text-xs font-medium shadow-sm backdrop-blur-sm hover:bg-white"
              >
                Zamijeni
              </Label>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => updateField("profileImageUrl", "")}
                className="bg-white/90 text-rose-600 shadow-sm backdrop-blur-sm hover:bg-white hover:text-rose-700"
              >
                <X className="size-4" />
              </Button>
            </div>
          </div>
        ) : (
          <Label
            htmlFor="profile-photo-input"
            className={cn(
              "flex h-48 w-40 cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed transition-colors sm:h-56 sm:w-48 font-normal",
              isUploading
                ? "pointer-events-none border-border bg-muted/30"
                : "border-border/60 hover:border-primary/40 hover:bg-primary/3",
            )}
          >
            {isUploading ? (
              <CircleNotch className="size-7 animate-spin text-primary/40" />
            ) : (
              <ImageSquare className="size-7 text-muted-foreground/30" />
            )}
            <p className="mt-2 text-xs text-muted-foreground">
              {isUploading ? "Upload..." : "Dodaj fotografiju"}
            </p>
            <p className="mt-0.5 text-[10px] text-muted-foreground/50">
              JPG, PNG, WebP · maks. 5 MB
            </p>
          </Label>
        )}

        {uploadError && (
          <p className="text-xs text-rose-600">{uploadError}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="profileVideoUrl">
          Intro video URL{" "}
          <span className="text-xs font-normal text-muted-foreground">
            (opciono)
          </span>
        </Label>
        <Input
          id="profileVideoUrl"
          type="url"
          placeholder="https://youtube.com/watch?v=..."
          value={form.profileVideoUrl}
          onChange={(e) => updateField("profileVideoUrl", e.target.value)}
        />
        {form.profileVideoUrl.trim() && !isValidProfileVideoUrl(form.profileVideoUrl.trim()) ? (
          <p className="text-xs text-rose-600">{PROFILE_VIDEO_ERROR}</p>
        ) : (
          <p className="text-xs text-muted-foreground">
            Link na YouTube ili Vimeo video u kojem se predstavljate.
          </p>
        )}
      </div>
    </div>
  );
}
